/**
 * Authed fetch for durable site-ingest artifacts
 * (`GET /api/v1/ontology/documents/download?key=…`).
 *
 * Keys come from the proxy list response and must not be reconstructed
 * in the browser.
 */

import { PROXY_API_V1_URL, createOntologyHeaders } from '../core'
import { SourceDocUnavailableError } from './source-doc'

export interface FetchSiteStoredDocumentResult {
  blob: Blob
  contentType: string
}

/** Build the absolute proxy URL for a stored site document download. */
export function siteStoredDocumentPath(key: string): string {
  const params = new URLSearchParams()
  params.set('key', key)
  return `${PROXY_API_V1_URL}/ontology/documents/download?${params.toString()}`
}

/**
 * Fetch site-stored document bytes by opaque object key.
 * Maps HTTP 404 to {@link SourceDocUnavailableError}.
 */
export async function fetchSiteStoredDocument(key: string): Promise<FetchSiteStoredDocumentResult> {
  const trimmed = key.trim()
  if (!trimmed) {
    throw new SourceDocUnavailableError('Document key is missing')
  }
  const headers = createOntologyHeaders()
  headers.delete('Content-Type')
  const res = await fetch(siteStoredDocumentPath(trimmed), { headers })
  if (res.status === 404) {
    throw new SourceDocUnavailableError('Document is not available for this site')
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`site document fetch failed (${res.status})${text ? `: ${text}` : ''}`)
  }
  const contentType = res.headers.get('Content-Type')?.split(';')[0]?.trim() || ''
  return {
    blob: await res.blob(),
    contentType
  }
}
